// Dummy data for the Insights issues report.
// Shapes mirror the real issue statistics contracts in @views/Issues/interfaces so the
// report can later be switched to the real endpoints without changing components.
// All data is deterministic (seeded) so screenshots and reviews stay stable.
// Each account in the subscription gets its own generated report per month; the subscription
// report is the aggregate of all its accounts.

import { IssueStatus } from '@views/Issues/interfaces';
import type {
    CountPerIssueStatus,
    IssueReportCounts,
    IssueDailyStatistics,
    IssueStatisticsLink,
    UserAgentStats,
    GeoIpTotalStats,
    TopSubscriber,
    UnsubscribeReasonStats,
} from '@views/Issues/interfaces';

export interface InsightsAccountReference {
    AccountId: string;
    Name: string;
}

export interface InsightsSubscription {
    SubscriptionId: string;
    Name: string;
    Accounts: InsightsAccountReference[];
}

export type InsightsReportScope = { Type: 'subscription' } | { Type: 'accounts'; AccountIds: string[] };

export interface InsightsIssueSummary {
    IssueId: string;
    Account: InsightsAccountReference;
    Name: string;
    Subject: string;
    Status: IssueStatus;
    Sent: string;
    Counts: IssueReportCounts;
}

export interface InsightsMonthlyTrend {
    Month: string;
    IssueCount: number;
    SentCount: number;
    OpenPercentage: number;
    ReceivedPercentage: number;
    ClickPercentage: number;
    BouncePercentage: number;
    UnsubscribePercentage: number;
    InactivePercentage: number;
}

export interface InsightsIssuesReportData {
    CountPerStatus: CountPerIssueStatus;
    Totals: IssueReportCounts;
    RecentIssues: InsightsIssueSummary[];
    MonthlyTrend: InsightsMonthlyTrend[];
    EventsOverTime: IssueDailyStatistics[];
    TopLinks: IssueStatisticsLink[];
    MailClients: UserAgentStats;
    GeoIp: GeoIpTotalStats;
    TopSubscribers: TopSubscriber[];
    UnsubscribeReasons: UnsubscribeReasonStats[];
}

// Mulberry32 — small deterministic PRNG
function createRandom(seed: number): () => number {
    let state = seed;
    return () => {
        state |= 0;
        state = (state + 0x6d2b79f5) | 0;
        let t = Math.imul(state ^ (state >>> 15), 1 | state);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// Reseeded per account in buildAccountReport so each account is stable on its own
let random = createRandom(20261021);

function between(min: number, max: number): number {
    return min + random() * (max - min);
}

function round2(value: number): number {
    return Math.round(value * 100) / 100;
}

function percentageOf(count: number, sentCount: number): number {
    return sentCount > 0 ? round2((count / sentCount) * 100) : 0;
}

function fakeGuid(index: number): string {
    const hex = (index * 2654435761).toString(16).padStart(8, '0').slice(-8);
    return `${hex}-0000-4000-8000-${hex.padStart(12, '0')}`;
}

// Percentages are relative to SentCount, matching IssueStatisticsFacade.
function buildCounts(recipientCount: number, rates: { bounce: number; open: number; click: number; conversion: number; unsubscribe: number }): IssueReportCounts {
    const sentCount = Math.round(recipientCount * between(0.985, 0.998));
    const bounceCount = Math.round(sentCount * rates.bounce);
    const receivedCount = sentCount - bounceCount;
    const openCount = Math.round(receivedCount * rates.open);
    const clickCount = Math.round(openCount * rates.click);
    const openClickCount = openCount + Math.round(clickCount * 0.04);
    const conversionCount = Math.round(clickCount * rates.conversion);
    const unsubscribeCount = Math.round(receivedCount * rates.unsubscribe);
    const inactiveCount = receivedCount - openClickCount;

    return {
        RecipientCount: recipientCount,
        SentCount: sentCount,
        ReceivedCount: receivedCount,
        OpenCount: openCount,
        ClickCount: clickCount,
        ConversionCount: conversionCount,
        BounceCount: bounceCount,
        UnsubscribeCount: unsubscribeCount,
        InactiveCount: inactiveCount,
        OpenClickCount: openClickCount,
        SentPercentage: percentageOf(sentCount, recipientCount),
        ReceivedPercentage: percentageOf(receivedCount, sentCount),
        OpenPercentage: percentageOf(openCount, sentCount),
        ClickPercentage: percentageOf(clickCount, sentCount),
        ConversionPercentage: percentageOf(conversionCount, sentCount),
        BouncePercentage: percentageOf(bounceCount, sentCount),
        UnsubscribePercentage: percentageOf(unsubscribeCount, sentCount),
        InactivePercentage: percentageOf(inactiveCount, sentCount),
        OpenClickPercentage: percentageOf(openClickCount, sentCount),
    };
}

function randomRates() {
    return {
        bounce: between(0.004, 0.02),
        open: between(0.32, 0.58),
        click: between(0.08, 0.22),
        conversion: between(0.02, 0.09),
        unsubscribe: between(0.001, 0.006),
    };
}

function sumCounts(items: IssueReportCounts[]): IssueReportCounts {
    const sum = (pick: (c: IssueReportCounts) => number) => items.reduce((acc, c) => acc + pick(c), 0);
    const sentCount = sum((c) => c.SentCount);
    const recipientCount = sum((c) => c.RecipientCount);
    const receivedCount = sum((c) => c.ReceivedCount);
    const openCount = sum((c) => c.OpenCount);
    const clickCount = sum((c) => c.ClickCount);
    const conversionCount = sum((c) => c.ConversionCount);
    const bounceCount = sum((c) => c.BounceCount);
    const unsubscribeCount = sum((c) => c.UnsubscribeCount);
    const inactiveCount = sum((c) => c.InactiveCount);
    const openClickCount = sum((c) => c.OpenClickCount);

    return {
        RecipientCount: recipientCount,
        SentCount: sentCount,
        ReceivedCount: receivedCount,
        OpenCount: openCount,
        ClickCount: clickCount,
        ConversionCount: conversionCount,
        BounceCount: bounceCount,
        UnsubscribeCount: unsubscribeCount,
        InactiveCount: inactiveCount,
        OpenClickCount: openClickCount,
        SentPercentage: percentageOf(sentCount, recipientCount),
        ReceivedPercentage: percentageOf(receivedCount, sentCount),
        OpenPercentage: percentageOf(openCount, sentCount),
        ClickPercentage: percentageOf(clickCount, sentCount),
        ConversionPercentage: percentageOf(conversionCount, sentCount),
        BouncePercentage: percentageOf(bounceCount, sentCount),
        UnsubscribePercentage: percentageOf(unsubscribeCount, sentCount),
        InactivePercentage: percentageOf(inactiveCount, sentCount),
        OpenClickPercentage: percentageOf(openClickCount, sentCount),
    };
}

const TODAY = new Date('2026-10-21T00:00:00Z');

function monthKey(date: Date): string {
    return date.toISOString().slice(0, 7);
}

// Days of the month as yyyy-MM-dd
function daysInMonth(month: string): string[] {
    const days: string[] = [];
    const date = new Date(`${month}-01T00:00:00Z`);
    while (monthKey(date) === month) {
        days.push(date.toISOString().slice(0, 10));
        date.setUTCDate(date.getUTCDate() + 1);
    }
    return days;
}

const ISSUE_NAMES: { name: string; subject: string }[] = [
    { name: 'Nyhetsbrev oktober', subject: 'Höstens nyheter är här 🍂' },
    { name: 'Black Week teaser', subject: 'Snart smäller det – spara datumet!' },
    { name: 'Webinar-inbjudan: AI i marknadsföring', subject: 'Välkommen på webinar 5 november' },
    { name: 'Påminnelse webinar', subject: 'Imorgon kör vi – här är länken' },
    { name: 'Kundundersökning Q3', subject: 'Tyck till och vinn ett presentkort' },
    { name: 'Nyhetsbrev september', subject: 'Nytt i september: segment och resor' },
    { name: 'Produktlansering: Insights', subject: 'Se hur dina utskick presterar – på ett ställe' },
    { name: 'Återaktivering inaktiva', subject: 'Vi saknar dig! Här är 20 % rabatt' },
    { name: 'Eventbekräftelse Stockholm', subject: 'Din plats är bokad' },
    { name: 'Nyhetsbrev augusti', subject: 'Tillbaka efter sommaren' },
    { name: 'Sommarkampanj sista chansen', subject: 'Sista dagen för sommarpriser' },
    { name: 'Partnerutskick juni', subject: 'Nyheter för våra partners' },
];

// Number of months that can be picked; each report also shows the 12-month trend leading up to it
const REPORT_MONTH_COUNT = 12;
const TREND_MONTH_COUNT = 12;

// Completed months only, oldest first, ending with last month
const MONTHS = Array.from({ length: REPORT_MONTH_COUNT + TREND_MONTH_COUNT - 1 }, (_, i) => {
    const date = new Date(TODAY);
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() - (REPORT_MONTH_COUNT + TREND_MONTH_COUNT - 1 - i));
    return monthKey(date);
});

// Seasonal volume per calendar month (Jan–Dec): dip in July, peaks around Nov/Dec
const MONTH_VOLUME = [0.85, 0.9, 1.0, 1.0, 1.05, 0.8, 0.45, 0.85, 1.1, 1.0, 1.35, 1.2];

function monthVolume(month: string): number {
    return MONTH_VOLUME[Number(month.slice(5, 7)) - 1];
}


interface DummyAccount extends InsightsAccountReference {
    Seed: number;
    // Relative send volume; 1 ≈ a mid-sized account
    Volume: number;
    Domain: string;
}

const DUMMY_ACCOUNTS: DummyAccount[] = [
    { AccountId: fakeGuid(1001), Name: 'Nordic Retail AB', Seed: 1101, Volume: 1, Domain: 'www.nordicretail.se' },
    { AccountId: fakeGuid(1002), Name: 'Nordic Retail Norge', Seed: 1102, Volume: 0.45, Domain: 'www.nordicretail.no' },
    { AccountId: fakeGuid(1003), Name: 'Nordic Retail Outlet', Seed: 1103, Volume: 0.7, Domain: 'outlet.nordicretail.se' },
    { AccountId: fakeGuid(1004), Name: 'Nordic Retail B2B', Seed: 1104, Volume: 0.25, Domain: 'b2b.nordicretail.se' },
    { AccountId: fakeGuid(1005), Name: 'Nordic Retail Events', Seed: 1105, Volume: 0.15, Domain: 'event.nordicretail.se' },
];

const SUBSCRIPTION: InsightsSubscription = {
    SubscriptionId: fakeGuid(1000),
    Name: 'Nordic Retail Group',
    Accounts: DUMMY_ACCOUNTS.map(({ AccountId, Name }) => ({ AccountId, Name })),
};

function shuffle<T>(items: T[]): T[] {
    const result = [...items];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

// Applies per-account jitter to a base distribution, then scales it to a total
function distribute(weights: number[], total: number): number[] {
    const jittered = weights.map((w) => w * between(0.7, 1.3));
    const sum = jittered.reduce((acc, w) => acc + w, 0);
    return jittered.map((w) => Math.round((w / sum) * total));
}

function buildRecentIssues(account: DummyAccount, month: InsightsMonthlyTrend, monthIndex: number): InsightsIssueSummary[] {
    const days = daysInMonth(month.Month);
    const count = Math.min(ISSUE_NAMES.length, month.IssueCount);
    const reference = { AccountId: account.AccountId, Name: account.Name };
    return shuffle(ISSUE_NAMES)
        .slice(0, count)
        .map((item, index) => {
            const recipientCount = Math.round((between(1800, 42000) * account.Volume) / 10) * 10;
            const day = days[Math.floor(random() * days.length)];
            const hour = String(8 + Math.round(between(0, 6))).padStart(2, '0');
            return {
                IssueId: fakeGuid(account.Seed * 10000 + monthIndex * 100 + index),
                Account: reference,
                Name: item.name,
                Subject: item.subject,
                Status: IssueStatus.Sent,
                Sent: `${day}T${hour}:00:00.000Z`,
                Counts: buildCounts(Math.max(200, recipientCount), randomRates()),
            };
        });
}

// Raw counts are kept alongside the rates so months can be aggregated across accounts
interface DummyMonth extends InsightsMonthlyTrend {
    ReceivedCount: number;
    OpenCount: number;
    ClickCount: number;
    BounceCount: number;
    UnsubscribeCount: number;
    InactiveCount: number;
}

function buildMonthlyTrend(account: DummyAccount): DummyMonth[] {
    const baseOpen = between(0.34, 0.46);
    return MONTHS.map((month, index) => {
        const issueCount = Math.max(1, Math.round(14 * account.Volume * monthVolume(month) + between(-1, 1)));
        // Account-level rates drift slowly; per-issue noise averages out over a month
        const counts = buildCounts(Math.round(issueCount * between(9000, 14000)), {
            bounce: between(0.007, 0.011),
            open: baseOpen + index * 0.002 + between(-0.02, 0.02),
            click: 0.13 + between(-0.012, 0.012),
            conversion: between(0.04, 0.06),
            unsubscribe: between(0.0025, 0.0038),
        });
        return {
            Month: month,
            IssueCount: issueCount,
            SentCount: counts.SentCount,
            ReceivedCount: counts.ReceivedCount,
            OpenCount: counts.OpenCount,
            ClickCount: counts.ClickCount,
            BounceCount: counts.BounceCount,
            UnsubscribeCount: counts.UnsubscribeCount,
            InactiveCount: counts.InactiveCount,
            ReceivedPercentage: counts.ReceivedPercentage,
            OpenPercentage: counts.OpenPercentage,
            ClickPercentage: counts.ClickPercentage,
            BouncePercentage: counts.BouncePercentage,
            UnsubscribePercentage: counts.UnsubscribePercentage,
            InactivePercentage: counts.InactivePercentage,
        };
    });
}

function buildCountPerStatus(account: DummyAccount, trend: InsightsMonthlyTrend[]): CountPerIssueStatus {
    const scaled = (base: number) => Math.round(base * account.Volume * between(0.6, 1.4));
    return {
        DraftIssuesCount: scaled(17),
        ScheduledIssuesCount: scaled(4),
        SendingIssuesCount: random() < account.Volume ? 1 : 0,
        FailedIssuesCount: scaled(1),
        PausedIssuesCount: random() < 0.3 ? 1 : 0,
        SentIssuesCount: trend.reduce((acc, m) => acc + m.IssueCount, 0),
        TemplateIssuesCount: scaled(23),
        IssueVariationCount: scaled(6),
        RecurringIssuesCount: scaled(3),
    };
}

// TypeName values match the event keys used in STATISTICS_EVENT_TYPES
function buildEventsOverTime(account: DummyAccount, month: string): IssueDailyStatistics[] {
    const series: Record<string, { Date: string; Count: number }[]> = { sent: [], opened: [], clicked: [], bounced: [], unsubscribed: [] };
    // Accounts send on different weekdays so the aggregate has a realistic spread
    const sendDays = shuffle([1, 2, 3, 4, 5]).slice(0, 2);

    const volume = account.Volume * monthVolume(month);
    for (const date of daysInMonth(month)) {
        const weekday = new Date(date).getUTCDay();
        const isSendDay = sendDays.includes(weekday);
        const sent = Math.round((isSendDay ? between(9000, 26000) : between(0, 1500)) * volume);
        const opened = Math.round(sent * between(0.25, 0.4) + between(800, 2200) * volume);
        const clicked = Math.round(opened * between(0.08, 0.18));

        series.sent.push({ Date: date, Count: sent });
        series.opened.push({ Date: date, Count: opened });
        series.clicked.push({ Date: date, Count: clicked });
        series.bounced.push({ Date: date, Count: Math.round(sent * between(0.004, 0.015)) });
        series.unsubscribed.push({ Date: date, Count: Math.round(opened * between(0.002, 0.01)) });
    }

    return Object.entries(series).map(([TypeName, Values]) => ({ TypeName, Values }));
}

const LINK_PATHS = ['/kampanj/black-week', '/webinar/ai-marknadsforing', '/nyheter/insights', '/kundundersokning', '/produkter/hostkollektion', '/event/stockholm', '/'];

function buildTopLinks(account: DummyAccount, month: string): IssueStatisticsLink[] {
    return shuffle(LINK_PATHS)
        .slice(0, 5)
        .map((path) => {
            const contactCount = Math.round(between(400, 4200) * account.Volume * monthVolume(month));
            return { Url: `https://${account.Domain}${path}`, ContactCount: contactCount, ClickCount: Math.round(contactCount * between(1.02, 1.45)) };
        });
}

const MAIL_CLIENT_BASE: { Name: string; IsMobile: boolean; Weight: number }[] = [
    { Name: 'Apple Mail', IsMobile: false, Weight: 43 },
    { Name: 'iPhone', IsMobile: true, Weight: 23.7 },
    { Name: 'Gmail', IsMobile: false, Weight: 14.2 },
    { Name: 'Outlook', IsMobile: false, Weight: 10.2 },
    { Name: 'Android', IsMobile: true, Weight: 6.2 },
    { Name: 'Övriga', IsMobile: false, Weight: 2.7 },
];

function toMailClientStats(rows: { Name: string; IsMobile: boolean; ContactCount: number; OpenCount: number }[]): UserAgentStats {
    const totalOpens = rows.reduce((acc, r) => acc + r.OpenCount, 0);
    const totalContacts = rows.reduce((acc, r) => acc + r.ContactCount, 0);
    // Part of desktop-client opens (Apple Mail, Gmail) happen on phones, so mobile share exceeds IsMobile alone
    const mobileOpens = rows.reduce((acc, r) => acc + (r.IsMobile ? r.OpenCount : r.OpenCount * 0.4), 0);
    const mobilePercentage = Math.round((mobileOpens / Math.max(totalOpens, 1)) * 1000) / 10;
    return {
        MobilePercentage: mobilePercentage,
        DesktopPercentage: Math.round((100 - mobilePercentage) * 10) / 10,
        UserAgents: [...rows]
            .sort((a, b) => b.OpenCount - a.OpenCount)
            .map((r) => ({
                Name: r.Name,
                IsMobile: r.IsMobile,
                ContactCount: r.ContactCount,
                ContactPercentage: percentageOf(r.ContactCount, totalContacts),
                OpenCount: r.OpenCount,
                OpenPercentage: percentageOf(r.OpenCount, totalOpens),
            })),
    };
}

function buildMailClients(totalOpens: number): UserAgentStats {
    const opens = distribute(
        MAIL_CLIENT_BASE.map((c) => c.Weight),
        totalOpens
    );
    return toMailClientStats(MAIL_CLIENT_BASE.map((c, i) => ({ Name: c.Name, IsMobile: c.IsMobile, OpenCount: opens[i], ContactCount: Math.round(opens[i] * 0.72) })));
}

const COUNTRY_BASE = [
    { CountryName: 'Sweden', CountryCode: 'SE', Weight: 82.4 },
    { CountryName: 'Norway', CountryCode: 'NO', Weight: 6.0 },
    { CountryName: 'Finland', CountryCode: 'FI', Weight: 4.1 },
    { CountryName: 'Denmark', CountryCode: 'DK', Weight: 3.1 },
    { CountryName: 'Germany', CountryCode: 'DE', Weight: 2.0 },
    { CountryName: 'United States', CountryCode: 'US', Weight: 2.4 },
];

const CITY_BASE = [
    { CityName: 'Stockholm', CountryCode: 'SE', Weight: 29.5 },
    { CityName: 'Göteborg', CountryCode: 'SE', Weight: 13.1 },
    { CityName: 'Malmö', CountryCode: 'SE', Weight: 9.3 },
    { CityName: 'Uppsala', CountryCode: 'SE', Weight: 4.9 },
    { CityName: 'Oslo', CountryCode: 'NO', Weight: 3.9 },
    { CityName: 'Helsinki', CountryCode: 'FI', Weight: 2.7 },
];

function withGeoPercentages<T extends { OpenCount: number; ClickCount: number }>(rows: T[]): (T & { OpenPercentage: number; ClickPercentage: number })[] {
    const totalOpens = rows.reduce((acc, r) => acc + r.OpenCount, 0);
    const totalClicks = rows.reduce((acc, r) => acc + r.ClickCount, 0);
    return rows.map((r) => ({ ...r, OpenPercentage: percentageOf(r.OpenCount, totalOpens), ClickPercentage: percentageOf(r.ClickCount, totalClicks) }));
}

function buildGeoIp(account: DummyAccount, totalOpens: number): GeoIpTotalStats {
    // The Norwegian account is mostly opened in Norway
    const isNorwegian = account.Domain.endsWith('.no');
    const countryWeights = COUNTRY_BASE.map((c) => (!isNorwegian ? c.Weight : c.CountryCode === 'NO' ? 80 : c.CountryCode === 'SE' ? 8 : c.Weight));
    const cityWeights = CITY_BASE.map((c) => (!isNorwegian ? c.Weight : c.CityName === 'Oslo' ? 45 : c.Weight / 4));
    const countryOpens = distribute(countryWeights, totalOpens);
    const cityOpens = distribute(cityWeights, Math.round(totalOpens * 0.63));
    return {
        GeoIpCountryStats: withGeoPercentages(
            COUNTRY_BASE.map((c, i) => ({ CountryName: c.CountryName, CountryCode: c.CountryCode, OpenCount: countryOpens[i], ClickCount: Math.round(countryOpens[i] * between(0.11, 0.16)) }))
        ),
        GeoIpCityStats: withGeoPercentages(
            CITY_BASE.map((c, i) => ({ CityName: c.CityName, CountryCode: c.CountryCode, OpenCount: cityOpens[i], ClickCount: Math.round(cityOpens[i] * between(0.11, 0.17)) }))
        ),
    };
}

const SUBSCRIBER_NAMES: [string, string][] = [
    ['Anna', 'Lindqvist'],
    ['Erik', 'Johansson'],
    ['Sara', 'Berg'],
    ['Johan', 'Nilsson'],
    ['Maria', 'Karlsson'],
    ['Lars', 'Andersson'],
    ['Emma', 'Svensson'],
    ['Oskar', 'Persson'],
    ['Ingrid', 'Hansen'],
    ['Mikael', 'Virtanen'],
    ['Elin', 'Sjöberg'],
    ['Nils', 'Eriksson'],
];

function buildTopSubscribers(account: DummyAccount): TopSubscriber[] {
    const tld = account.Domain.split('.').slice(-1)[0];
    return shuffle(SUBSCRIBER_NAMES)
        .slice(0, 8)
        .map(([firstName, lastName], index) => {
            const openCount = Math.round(between(3, 9));
            const clickCount = Math.round(openCount * between(0.4, 0.75));
            return {
                ContactId: fakeGuid(account.Seed * 100 + 50 + index),
                FirstName: firstName,
                LastName: lastName,
                Email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.${tld}`,
                PhotoUrl: null,
                OpenCount: openCount,
                ClickCount: clickCount,
                TotalClickCount: Math.round(clickCount * 1.4),
            };
        })
        .sort((a, b) => b.OpenCount - a.OpenCount);
}

// ReasonValue 1–6 as labelled in StatisticsUnsubscribeReasons (NoLongerWant … Other)
const UNSUBSCRIBE_REASON_WEIGHTS = [412, 268, 91, 37, 24, 143];

// Roughly a third of unsubscribers answer why
function buildUnsubscribeReasons(month: DummyMonth): UnsubscribeReasonStats[] {
    const counts = distribute(UNSUBSCRIBE_REASON_WEIGHTS, Math.round(month.UnsubscribeCount * 0.35));
    return counts.map((Count, index) => ({ ReasonValue: index + 1, Count }));
}

// The full trend is generated once per account so every month's report agrees on the numbers
const ACCOUNT_TRENDS = new Map(
    DUMMY_ACCOUNTS.map((a) => {
        random = createRandom(a.Seed);
        return [a.AccountId, buildMonthlyTrend(a)];
    })
);

function buildAccountReport(account: DummyAccount, month: string): InsightsIssuesReportData {
    const fullTrend = ACCOUNT_TRENDS.get(account.AccountId) as DummyMonth[];
    const monthIndex = MONTHS.indexOf(month);
    const trend = fullTrend.slice(monthIndex - TREND_MONTH_COUNT + 1, monthIndex + 1);
    const current = fullTrend[monthIndex];

    random = createRandom(account.Seed * 100 + monthIndex);
    const recentIssues = buildRecentIssues(account, current, monthIndex).sort((a, b) => b.Sent.localeCompare(a.Sent));

    return {
        CountPerStatus: buildCountPerStatus(account, trend),
        Totals: sumCounts(recentIssues.map((i) => i.Counts)),
        RecentIssues: recentIssues,
        MonthlyTrend: trend,
        EventsOverTime: buildEventsOverTime(account, month),
        TopLinks: buildTopLinks(account, month),
        MailClients: buildMailClients(current.OpenCount),
        GeoIp: buildGeoIp(account, current.OpenCount),
        TopSubscribers: buildTopSubscribers(account),
        UnsubscribeReasons: buildUnsubscribeReasons(current),
    };
}

function mergeBy<T>(rows: T[], key: (row: T) => string, merge: (a: T, b: T) => T): T[] {
    const map = new Map<string, T>();
    for (const row of rows) {
        const existing = map.get(key(row));
        map.set(key(row), existing ? merge(existing, row) : row);
    }
    return [...map.values()];
}

function sumEngagement<T extends { OpenCount: number; ClickCount: number }>(a: T, b: T): T {
    return { ...a, OpenCount: a.OpenCount + b.OpenCount, ClickCount: a.ClickCount + b.ClickCount };
}

// Subscription report = aggregate of all account reports; rates are recomputed from summed counts
function aggregateReports(reports: InsightsIssuesReportData[]): InsightsIssuesReportData {
    const all = <T>(pick: (r: InsightsIssuesReportData) => T[]) => reports.flatMap(pick);

    const countPerStatus = reports
        .map((r) => r.CountPerStatus)
        .reduce((acc, c) => {
            const result = { ...acc };
            (Object.keys(c) as (keyof CountPerIssueStatus)[]).forEach((k) => (result[k] = acc[k] + c[k]));
            return result;
        });

    const monthlyTrend: DummyMonth[] = reports[0].MonthlyTrend.map(({ Month: month }) => {
        const months = reports.map((r) => r.MonthlyTrend.find((m) => m.Month === month) as DummyMonth);
        const sum = (pick: (m: DummyMonth) => number) => months.reduce((acc, m) => acc + pick(m), 0);
        const sentCount = sum((m) => m.SentCount);
        const receivedCount = sum((m) => m.ReceivedCount);
        const openCount = sum((m) => m.OpenCount);
        const clickCount = sum((m) => m.ClickCount);
        const bounceCount = sum((m) => m.BounceCount);
        const unsubscribeCount = sum((m) => m.UnsubscribeCount);
        const inactiveCount = sum((m) => m.InactiveCount);
        return {
            Month: month,
            IssueCount: sum((m) => m.IssueCount),
            SentCount: sentCount,
            ReceivedCount: receivedCount,
            OpenCount: openCount,
            ClickCount: clickCount,
            BounceCount: bounceCount,
            UnsubscribeCount: unsubscribeCount,
            InactiveCount: inactiveCount,
            ReceivedPercentage: percentageOf(receivedCount, sentCount),
            OpenPercentage: percentageOf(openCount, sentCount),
            ClickPercentage: percentageOf(clickCount, sentCount),
            BouncePercentage: percentageOf(bounceCount, sentCount),
            UnsubscribePercentage: percentageOf(unsubscribeCount, sentCount),
            InactivePercentage: percentageOf(inactiveCount, sentCount),
        };
    });

    const eventsOverTime = reports[0].EventsOverTime.map((series) => ({
        TypeName: series.TypeName,
        Values: series.Values.map((value, i) => ({
            Date: value.Date,
            Count: reports.reduce((acc, r) => acc + (r.EventsOverTime.find((s) => s.TypeName === series.TypeName)?.Values[i].Count ?? 0), 0),
        })),
    }));

    const recentIssues = all((r) => r.RecentIssues)
        .sort((a, b) => b.Sent.localeCompare(a.Sent))
        .slice(0, 12);

    return {
        CountPerStatus: countPerStatus,
        Totals: sumCounts(recentIssues.map((i) => i.Counts)),
        RecentIssues: recentIssues,
        MonthlyTrend: monthlyTrend,
        EventsOverTime: eventsOverTime,
        TopLinks: all((r) => r.TopLinks)
            .sort((a, b) => b.ClickCount - a.ClickCount)
            .slice(0, 7),
        MailClients: toMailClientStats(
            mergeBy(
                all((r) => r.MailClients.UserAgents),
                (u) => u.Name,
                (a, b) => ({ ...a, ContactCount: a.ContactCount + b.ContactCount, OpenCount: a.OpenCount + b.OpenCount })
            )
        ),
        GeoIp: {
            GeoIpCountryStats: withGeoPercentages(mergeBy(all((r) => r.GeoIp.GeoIpCountryStats), (c) => c.CountryCode, sumEngagement)),
            GeoIpCityStats: withGeoPercentages(mergeBy(all((r) => r.GeoIp.GeoIpCityStats), (c) => `${c.CountryCode}-${c.CityName}`, sumEngagement)),
        },
        TopSubscribers: mergeBy(
            all((r) => r.TopSubscribers),
            (s) => s.Email,
            (a, b) => ({ ...sumEngagement(a, b), TotalClickCount: a.TotalClickCount + b.TotalClickCount })
        )
            .sort((a, b) => b.OpenCount + b.ClickCount - (a.OpenCount + a.ClickCount))
            .slice(0, 8),
        UnsubscribeReasons: mergeBy(
            all((r) => r.UnsubscribeReasons),
            (u) => String(u.ReasonValue),
            (a, b) => ({ ...a, Count: a.Count + b.Count })
        ),
    };
}

const ACCOUNT_REPORTS = new Map<string, InsightsIssuesReportData>();

function getAccountReport(accountId: string, month: string): InsightsIssuesReportData {
    const key = `${accountId}|${month}`;
    let report = ACCOUNT_REPORTS.get(key);
    if (!report) {
        const account = DUMMY_ACCOUNTS.find((a) => a.AccountId === accountId);
        if (!account) throw new Error(`Unknown account ${accountId}`);
        report = buildAccountReport(account, month);
        ACCOUNT_REPORTS.set(key, report);
    }
    return report;
}

// Async wrappers so the report can swap to real API calls without changing call sites
export async function getInsightsSubscription(): Promise<InsightsSubscription> {
    return SUBSCRIPTION;
}

// Months that have a report, newest first (yyyy-MM)
export async function getInsightsReportMonths(): Promise<string[]> {
    return MONTHS.slice(-REPORT_MONTH_COUNT).reverse();
}

// month: yyyy-MM
export async function getInsightsIssuesReport(scope: InsightsReportScope, month: string): Promise<InsightsIssuesReportData> {
    if (!MONTHS.slice(-REPORT_MONTH_COUNT).includes(month)) throw new Error(`No report for ${month}`);
    const accountIds = scope.Type === 'subscription' ? DUMMY_ACCOUNTS.map((a) => a.AccountId) : scope.AccountIds;
    const reports = accountIds.map((accountId) => getAccountReport(accountId, month));
    return reports.length === 1 ? reports[0] : aggregateReports(reports);
}
